/**
 * OTIF Grade Thresholds hook - CR-6267
 *
 * Owns CRUD for the configurable OTIF colour bands. Kept separate from
 * `usePerformanceSettings` so an OTIF band edit never round-trips the whole
 * settings form — and can never write a supplier rating grade by accident.
 */
import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { performanceSettingsAPI, OTIFGradeThreshold, CreateOTIFGradeThresholdData } from '../../../lib/api';
import { getApiErrorMessage } from '../../../utils/errorUtils';
import { useConfirm, useAlertDialog } from '../../../contexts/ConfirmDialogContext';
import { useSettingsScope } from './SettingsScopeContext';
import { otifLimits, shownMax, storedMaxText } from '../../../lib/gradeBandRange';

/** Editable shape of a band while its inline form is open. */
export interface OTIFGradeEditData {
  grade: string;
  label: string;
  min_otif_percent: string;
  max_otif_percent: string;
  color: string;
  description: string;
  order: number;
}

const BLANK_BAND: CreateOTIFGradeThresholdData = {
  grade: '',
  label: '',
  min_otif_percent: '0',
  max_otif_percent: '100',
  color: '#3B82F6',
  description: '',
  order: 0,
};

/**
 * Manage the OTIF colour grade bands for the company being configured.
 *
 * @param bands - Bands from the loaded performance settings payload
 * @param onChanged - Called after any successful mutation so the parent can refetch
 * @returns Band edit state plus handlers for edit/add/delete/reset
 */
export function useOTIFGradeThresholds(
  bands: OTIFGradeThreshold[],
  onChanged: () => Promise<void> | void,
) {
  const confirm = useConfirm();
  // CR-7271: a refused band write is explained in a modal the user must
  // dismiss, not a toast that slides away mid-sentence.
  const alertDialog = useAlertDialog();
  // CR-7274 rework: every write names the scope the picker is configuring.
  const scopeTarget = useSettingsScope();

  const [editingBand, setEditingBand] = useState<number | null>(null);
  const [bandEditData, setBandEditData] = useState<OTIFGradeEditData | null>(null);
  const [savingBand, setSavingBand] = useState(false);
  const [showAddBand, setShowAddBand] = useState(false);
  const [newBandData, setNewBandData] = useState<CreateOTIFGradeThresholdData>({ ...BLANK_BAND });
  const [addingBand, setAddingBand] = useState(false);
  const [resettingBands, setResettingBands] = useState(false);

  const handleEditBand = useCallback((band: OTIFGradeThreshold) => {
    setEditingBand(band.id);
    setBandEditData({
      grade: band.grade,
      label: band.label,
      min_otif_percent: band.min_otif_percent,
      // CR-8677: the max as listed — one step below the band above's min.
      max_otif_percent: shownMax(otifLimits(band), bands.map(otifLimits)).toFixed(2),
      color: band.color,
      description: band.description,
      order: band.order,
    });
  }, [bands]);

  const handleCancelEditBand = useCallback(() => {
    setEditingBand(null);
    setBandEditData(null);
  }, []);

  const handleBandFieldChange = useCallback((field: string, value: string | number) => {
    setBandEditData(prev => (prev ? { ...prev, [field]: value } : null));
  }, []);

  const handleSaveBand = useCallback(async (bandId: number) => {
    if (!bandEditData) return;
    const saved = bands.find((b) => b.id === bandId);

    try {
      setSavingBand(true);
      await performanceSettingsAPI.updateOTIFGradeThreshold(bandId, {
        grade: bandEditData.grade,
        label: bandEditData.label,
        min_otif_percent: bandEditData.min_otif_percent,
        max_otif_percent: storedMaxText(bandEditData.max_otif_percent, saved ? otifLimits(saved) : null, bands.map(otifLimits)),
        color: bandEditData.color,
        description: bandEditData.description,
        order: bandEditData.order,
      }, scopeTarget);
      await onChanged();
      setEditingBand(null);
      setBandEditData(null);
      toast.success('OTIF grade updated successfully');
    } catch (error: unknown) {
      console.error('Failed to update OTIF grade:', error);
      await alertDialog({ title: 'OTIF grade not saved', description: getApiErrorMessage(error, 'Failed to update OTIF grade') });
    } finally {
      setSavingBand(false);
    }
  }, [bandEditData, bands, onChanged, alertDialog, scopeTarget]);

  const handleNewBandFieldChange = useCallback((field: string, value: string | number) => {
    setNewBandData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleAddBand = useCallback(async () => {
    if (!newBandData.grade.trim()) {
      toast.error('Please enter a grade identifier');
      return;
    }

    try {
      setAddingBand(true);
      const maxOrder = bands.reduce((max, b) => Math.max(max, b.order), 0);
      await performanceSettingsAPI.createOTIFGradeThreshold({
        ...newBandData,
        max_otif_percent: storedMaxText(newBandData.max_otif_percent, null, bands.map(otifLimits)),
        order: maxOrder + 1,
      }, scopeTarget);
      await onChanged();
      setShowAddBand(false);
      setNewBandData({ ...BLANK_BAND });
      toast.success('New OTIF grade added successfully');
    } catch (error: unknown) {
      console.error('Failed to add OTIF grade:', error);
      await alertDialog({ title: 'OTIF grade not added', description: getApiErrorMessage(error, 'Failed to add OTIF grade') });
    } finally {
      setAddingBand(false);
    }
  }, [newBandData, bands, onChanged, alertDialog, scopeTarget]);

  const handleDeleteBand = useCallback(async (bandId: number, label: string) => {
    const ok = await confirm({
      title: 'Delete OTIF Grade',
      description: `Are you sure you want to delete the "${label}" OTIF grade? Dashboards will stop using its colour.`,
      confirmLabel: 'Delete',
      variant: 'destructive',
    });
    if (!ok) return;

    try {
      await performanceSettingsAPI.deleteOTIFGradeThreshold(bandId, scopeTarget);
      await onChanged();
      setEditingBand(null);
      setBandEditData(null);
      toast.success('OTIF grade deleted successfully');
    } catch (error: unknown) {
      console.error('Failed to delete OTIF grade:', error);
      await alertDialog({ title: 'OTIF grade not deleted', description: getApiErrorMessage(error, 'Failed to delete OTIF grade') });
    }
  }, [confirm, onChanged, alertDialog, scopeTarget]);

  const handleResetBands = useCallback(async () => {
    const ok = await confirm({
      title: 'Reset OTIF Grades',
      description: 'This replaces every OTIF grade with the default set. Supplier Grade Thresholds are not affected.',
      confirmLabel: 'Reset',
      variant: 'destructive',
    });
    if (!ok) return;

    try {
      setResettingBands(true);
      await performanceSettingsAPI.resetOTIFGradeThresholds(scopeTarget);
      await onChanged();
      setEditingBand(null);
      setBandEditData(null);
      toast.success('OTIF grades reset to defaults');
    } catch (error: unknown) {
      console.error('Failed to reset OTIF grades:', error);
      await alertDialog({ title: 'OTIF grades not reset', description: getApiErrorMessage(error, 'Failed to reset OTIF grades') });
    } finally {
      setResettingBands(false);
    }
  }, [confirm, onChanged, alertDialog, scopeTarget]);

  return {
    editingBand,
    bandEditData,
    savingBand,
    showAddBand,
    newBandData,
    addingBand,
    resettingBands,
    setShowAddBand,
    handleEditBand,
    handleCancelEditBand,
    handleBandFieldChange,
    handleSaveBand,
    handleAddBand,
    handleDeleteBand,
    handleResetBands,
    handleNewBandFieldChange,
  };
}
